// Media manifest client (rebuild.md §9.11): media/manifest.json fetched network-first (the service worker
// caches it), with the bundled excerpt registry as a minimal fallback. Chooses a rendition per settings.
import { signal } from '@preact/signals';
import { EXCERPTS, FPS } from '@content/excerpts.ts';

export interface MediaExcerpt {
  id: string;
  stage: string;
  role: 'pre' | 'post';
  frames: number;
  seconds: number;
  markers: { at: number; label: string }[];
  video: Partial<Record<'480' | '720' | '1080', { url: string; bytes: number }>>;
  vtt: string | null;
  chapters: string | null;
  poster: string | null;
  thumb: string | null;
}

export interface MediaManifest {
  version: number;
  hash?: string;
  excerpts: MediaExcerpt[];
  packs: Record<string, { bytes: number; files: string[] }>;
  comic: string | null;
}

const fallback: MediaManifest = {
  version: 0,
  excerpts: EXCERPTS.map((e) => ({
    id: e.id,
    stage: e.stage,
    role: e.role,
    frames: e.out - e.in,
    seconds: (e.out - e.in) / FPS,
    markers: e.markers,
    video: {},
    vtt: null,
    chapters: null,
    poster: null,
    thumb: null,
  })),
  packs: {},
  comic: null,
};

export const mediaManifest = signal<MediaManifest>(fallback);
let loading: Promise<MediaManifest> | null = null;

export function loadMediaManifest(): Promise<MediaManifest> {
  loading ??= fetch('/media/manifest.json')
    .then(async (r) => {
      if (!r.ok) throw new Error(String(r.status));
      const m = (await r.json()) as MediaManifest;
      mediaManifest.value = m;
      return m;
    })
    .catch(() => fallback)
    .finally(() => {
      loading = null;
    });
  return loading;
}

export const mediaUrl = (rel: string | null): string | null => (rel ? `/media/${rel}` : null);

export function excerptMedia(id: string): MediaExcerpt | undefined {
  return mediaManifest.value.excerpts.find((e) => e.id === id);
}

type Quality = 'auto' | '720' | '1080' | '480';

/** Auto picks 1080 on ≥ 1280 px viewports with Save-Data off and a 4G-class connection; else 720. */
export function chooseRendition(e: MediaExcerpt, quality: Quality, downloaded: boolean): '480' | '720' | '1080' {
  if (downloaded && e.video['720']) return '720';
  if (quality !== 'auto') return e.video[quality] ? quality : '720';
  const conn = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  const fast = !conn || (!conn.saveData && (conn.effectiveType === undefined || conn.effectiveType === '4g'));
  if (conn?.saveData) return '480';
  return window.innerWidth >= 1280 && fast && e.video['1080'] ? '1080' : '720';
}
